<template>
  <div id="app">
    <!-- 登录页面：无布局 -->
    <router-view v-if="isLoginPage" />

    <!-- 其他页面：带布局 -->
    <div v-else :class="{ 'mobile-layout': isMobile }">
      <!-- 桌面端布局 -->
      <div v-if="!isMobile" class="desktop-layout">
        <el-container>
          <el-header class="header-desktop">
            <h2 class="header-title">📋 Tasker</h2>
            <div class="user-info">
              <span v-if="currentUser" class="user-name">👤 {{ currentUser.nickname || currentUser.username }}</span>
              <button class="btn-logout" @click="handleLogout">
                <span class="logout-icon">🚪</span>
                <span>退出登录</span>
              </button>
            </div>
          </el-header>
          <el-container>
            <el-aside width="200px" class="sidebar-desktop">
              <el-menu :default-active="$route.path" router class="menu-desktop">
                <el-menu-item index="/daily">
                  <span class="menu-icon">📅</span>
                  <span>每日计划</span>
                </el-menu-item>
                <el-menu-item index="/tasks">
                  <span class="menu-icon">📋</span>
                  <span>任务列表</span>
                </el-menu-item>
                <el-menu-item index="/ai">
                  <span class="menu-icon">🤖</span>
                  <span>AI 助手</span>
                </el-menu-item>
                <el-menu-item index="/notes">
                  <span class="menu-icon">📝</span>
                  <span>记事本</span>
                </el-menu-item>
                <el-menu-item index="/mood">
                  <span class="menu-icon">🌊</span>
                  <span>情绪释放</span>
                </el-menu-item>
              </el-menu>
            </el-aside>
            <el-main class="main-desktop">
              <router-view />
            </el-main>
          </el-container>
        </el-container>
      </div>

      <!-- 移动端布局 -->
      <div v-else class="mobile-layout">
        <!-- 顶部导航栏 -->
        <div class="mobile-header">
          <h1 class="mobile-title">{{ currentPageTitle }}</h1>
          <button v-if="currentUser" class="btn-logout-mobile" @click="handleLogout">退出</button>
        </div>

        <!-- 内容区域 -->
        <div class="mobile-content">
          <router-view />
        </div>

        <!-- 底部导航栏 -->
        <div class="mobile-tabbar">
          <router-link to="/daily" class="tabbar-item" :class="{ active: $route.path === '/daily' }">
            <span class="tabbar-icon">📅</span>
            <span class="tabbar-label">计划</span>
          </router-link>
          <router-link to="/tasks" class="tabbar-item" :class="{ active: $route.path === '/tasks' }">
            <span class="tabbar-icon">📋</span>
            <span class="tabbar-label">任务</span>
          </router-link>
          <router-link to="/ai" class="tabbar-item" :class="{ active: $route.path === '/ai' }">
            <span class="tabbar-icon">🤖</span>
            <span class="tabbar-label">AI</span>
          </router-link>
          <router-link to="/notes" class="tabbar-item" :class="{ active: $route.path === '/notes' }">
            <span class="tabbar-icon">📝</span>
            <span class="tabbar-label">记事</span>
          </router-link>
          <router-link to="/mood" class="tabbar-item" :class="{ active: $route.path === '/mood' }">
            <span class="tabbar-icon">🌊</span>
            <span class="tabbar-label">释放</span>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import * as userApi from './api/user'

export default {
  name: 'App',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const isMobile = ref(false)
    const currentUser = ref(null)

    const isLoginPage = computed(() => {
      return route.path === '/login'
    })

    const currentPageTitle = computed(() => {
      const titles = {
        '/daily': '📅 每日计划',
        '/tasks': '📋 任务列表',
        '/ai': '🤖 AI 助手',
        '/notes': '📝 记事本',
        '/mood': '🌊 情绪释放',
        '/login': '登录'
      }
      return titles[route.path] || '任务管理'
    })

    const checkMobile = () => {
      isMobile.value = window.innerWidth <= 768
    }

    const loadCurrentUser = async () => {
      // 如果在登录页，不加载用户信息
      if (route.path === '/login') {
        return
      }
      
      try {
        const res = await userApi.getCurrentUser()
        if (res.data.code === 200) {
          currentUser.value = res.data.data
        }
      } catch (error) {
        // 忽略错误，路由守卫会处理
      }
    }

    const handleLogout = async () => {
      try {
        await userApi.logout()
        currentUser.value = null
        localStorage.removeItem('user')
        ElMessage.success('已退出登录')
        router.push('/login')
      } catch (error) {
        ElMessage.error('退出失败')
      }
    }

    onMounted(() => {
      checkMobile()
      window.addEventListener('resize', checkMobile)
      loadCurrentUser()
    })

    onUnmounted(() => {
      window.removeEventListener('resize', checkMobile)
    })

    return {
      isMobile,
      isLoginPage,
      currentPageTitle,
      currentUser,
      handleLogout
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  position: relative;
}

#app {
  height: 100vh;
  height: 100dvh; /* 动态视口高度，解决移动端键盘问题 */
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', sans-serif;
}

/* ========== 桌面端样式 ========== */
.desktop-layout {
  height: 100%;
}

.el-container {
  height: 100%;
}

.header-desktop {
  background: linear-gradient(135deg, #007AFF 0%, #5856D6 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  box-shadow: 0 2px 12px rgba(0, 122, 255, 0.15);
}

.header-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.user-name {
  font-size: 14px;
  font-weight: 600;
}

.btn-logout {
  padding: 8px 16px;
  border: 2px solid white;
  border-radius: 8px;
  background: transparent;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-logout:hover {
  background: white;
  color: #007AFF;
}

.btn-logout-mobile {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  padding: 6px 12px;
  border: 1px solid #007AFF;
  border-radius: 8px;
  background: white;
  color: #007AFF;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 10;
}

.btn-logout-mobile:active {
  background: #007AFF;
  color: white;
  transform: translateY(-50%) scale(0.95);
}

.sidebar-desktop {
  background: #F2F2F7;
  border-right: 1px solid #D1D1D6;
}

.menu-desktop {
  border: none;
  background: transparent;
}

.menu-desktop .el-menu-item {
  margin: 8px 12px;
  border-radius: 12px;
  transition: all 0.2s;
}

.menu-desktop .el-menu-item:hover {
  background: rgba(0, 122, 255, 0.08);
}

.menu-desktop .el-menu-item.is-active {
  background: linear-gradient(135deg, #007AFF 0%, #5856D6 100%);
  color: white;
}

.menu-icon {
  font-size: 18px;
  margin-right: 8px;
}

.main-desktop {
  background: #F2F2F7;
  padding: 0;
  overflow-y: auto;
}

/* ========== 移动端样式 ========== */
.mobile-layout {
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh; /* 动态视口高度 */
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  background: #F2F2F7;
}

.mobile-header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 56px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  z-index: 100;
}

.mobile-title {
  font-size: 18px;
  font-weight: 700;
  color: #1d1d1f;
  margin: 0;
}

.mobile-content {
  flex: 1;
  overflow-y: auto;
  padding-top: 56px;
  padding-bottom: 60px;
  -webkit-overflow-scrolling: touch;
}

.mobile-tabbar {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 60px;
  background: white;
  display: flex;
  justify-content: space-around;
  align-items: center;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
  z-index: 100;
  padding-bottom: env(safe-area-inset-bottom);
}

.tabbar-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  text-decoration: none;
  color: #86868b;
  transition: all 0.2s;
  padding: 8px;
}

.tabbar-item.active {
  color: #007AFF;
}

.tabbar-icon {
  font-size: 24px;
  transition: transform 0.2s;
}

.tabbar-item.active .tabbar-icon {
  transform: scale(1.1);
}

.tabbar-label {
  font-size: 12px;
  font-weight: 600;
}

/* ========== 移动端页面内容优化 ========== */
@media (max-width: 768px) {
  /* 移除页面内边距，让内容全屏 */
  .daily-plan-container,
  .task-list-container {
    padding: 12px !important;
    min-height: auto !important;
  }

  /* 优化统计卡片 */
  .stats-grid {
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 8px !important;
  }

  .stat-card {
    padding: 12px !important;
  }

  .stat-icon {
    width: 40px !important;
    height: 40px !important;
    font-size: 20px !important;
  }

  .stat-value {
    font-size: 20px !important;
  }

  .stat-label {
    font-size: 12px !important;
  }

  /* 优化工具栏 */
  .toolbar,
  .toolbar-apple {
    padding: 12px !important;
    margin-bottom: 12px !important;
  }

  .page-title {
    font-size: 18px !important;
  }

  .date-badge,
  .task-count-badge {
    font-size: 12px !important;
    padding: 4px 8px !important;
  }

  /* 优化按钮 */
  .btn-apple,
  .btn-apple-primary,
  .btn-apple-success {
    padding: 8px 12px !important;
    font-size: 12px !important;
  }

  .btn-icon {
    font-size: 14px !important;
  }

  /* 优化进度条 */
  .progress-section {
    padding: 16px !important;
    margin-bottom: 12px !important;
  }

  .progress-title {
    font-size: 14px !important;
  }

  .progress-text {
    font-size: 12px !important;
  }

  /* 优化时间段标题 */
  .period-header-apple {
    padding: 12px !important;
    margin-bottom: 12px !important;
  }

  .period-icon-large {
    font-size: 24px !important;
  }

  .period-name-apple {
    font-size: 16px !important;
  }

  .period-time-apple {
    font-size: 12px !important;
  }

  .period-badge {
    font-size: 11px !important;
    padding: 4px 8px !important;
  }

  /* 优化任务卡片 */
  .task-card-apple,
  .task-card-list {
    padding: 12px !important;
    margin-bottom: 8px !important;
  }

  .task-title-apple,
  .task-title-list {
    font-size: 14px !important;
  }

  .task-meta-apple,
  .task-meta-list {
    font-size: 11px !important;
    gap: 8px !important;
  }

  /* 优化筛选标签 */
  .filter-section {
    padding: 12px !important;
    margin-bottom: 12px !important;
  }

  .filter-tab {
    padding: 8px 12px !important;
    font-size: 12px !important;
  }

  /* 优化分页 */
  .pagination-apple {
    padding: 12px !important;
  }

  /* 优化模态框 */
  .modal-apple {
    margin: 12px !important;
    max-height: 85vh !important;
  }

  .modal-header {
    padding: 16px !important;
  }

  .modal-title {
    font-size: 18px !important;
  }

  .modal-body {
    padding: 16px !important;
  }

  .modal-footer {
    padding: 16px !important;
  }

  /* 隐藏不必要的元素 */
  .smart-tip {
    display: none !important;
  }

  /* 优化任务网格 */
  .tasks-grid,
  .tasks-grid-apple {
    grid-template-columns: 1fr !important;
    gap: 8px !important;
  }
}

/* ========== 超小屏幕优化 ========== */
@media (max-width: 480px) {
  .mobile-header {
    height: 48px !important;
  }

  .mobile-title {
    font-size: 16px !important;
  }

  .mobile-content {
    padding-top: 48px !important;
  }

  .mobile-tabbar {
    height: 56px !important;
  }

  .tabbar-icon {
    font-size: 20px !important;
  }

  .tabbar-label {
    font-size: 10px !important;
  }
}
</style>
